import React, { useState } from 'react';
import { IconButton, styled, Typography } from '@mui/material';
import {
  Edit,
  EventSeatOutlined,
  LocalHospitalOutlined,
  MedicalServicesOutlined,
  SummarizeOutlined,
} from '@mui/icons-material';
import { moneyFormat, percentFormat } from 'helpers/moneyFormat';
import { usePermissionRules } from 'hooks/permissionRules';
import { useNewSurgerie } from '../../hooks/useNewSurgerie';
import EditAnesthetistValueDialog from '../../dialog/EditAnesthetistValueDialog';
import EditMarginDialog from '../../dialog/EditMarginDialog';
import {
  CardBody,
  GuidedCard,
  GuidedHeader,
  ItemRow,
  palette,
  SoftPanel,
  SummaryGrid,
} from '../../components/GuidedSurgerie';

const TotalPanel = styled('aside')({
  border: `1px solid ${palette.border}`,
  background: '#fff',
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  alignSelf: 'start',
  boxShadow: '0 12px 30px rgba(74, 14, 26, 0.07)',
});

const TotalValue = styled('div')({
  borderBottom: `1px solid ${palette.border}`,
  paddingBottom: 12,
});

const MiniRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
});

const LabelWithIcon = styled('div')({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  minWidth: 0,
});

const SurgerieSixthStep: React.FC = () => {
  const { surgerie } = useNewSurgerie();
  const [editAnesthetistPrice, setEditAnesthetistPrice] = useState(false);
  const [editMargin, setEditMargin] = useState(false);
  const { checkPermission } = usePermissionRules();

  return (
    <GuidedCard>
      {editAnesthetistPrice && <EditAnesthetistValueDialog onExited={() => setEditAnesthetistPrice(false)} />}
      {editMargin && <EditMarginDialog onExited={() => setEditMargin(false)} />}

      <GuidedHeader
        step={6}
        title="Resumo do orçamento"
        description="Confira todos os componentes antes de finalizar e gerar a versão de impressão."
      />

      <CardBody>
        <SummaryGrid>
          <SoftPanel>
            <Typography variant="subtitle2" gutterBottom>
              Composição
            </Typography>

            {surgerie.packages.map(item => (
              <ItemRow key={item.id}>
                <LabelWithIcon>
                  <SummarizeOutlined color="primary" fontSize="small" />
                  <div>
                    <Typography variant="body2" fontWeight={700}>
                      {item.package?.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pacote / produtos
                    </Typography>
                  </div>
                </LabelWithIcon>
                <Typography variant="caption" color="textSecondary">
                  Valor
                </Typography>
                <Typography variant="body2" fontWeight={700} color="success.main">
                  {moneyFormat(item.package?.sale_price || 0)}
                </Typography>
              </ItemRow>
            ))}

            <ItemRow>
              <LabelWithIcon>
                <MedicalServicesOutlined color="primary" fontSize="small" />
                <div>
                  <Typography variant="body2" fontWeight={700}>
                    Médico
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {surgerie.doctor?.doctor?.name || 'Não informado'}
                  </Typography>
                </div>
              </LabelWithIcon>
              <Typography variant="caption" color="textSecondary">
                Valor
              </Typography>
              <Typography variant="body2" fontWeight={700} color="success.main">
                {moneyFormat(surgerie.value_doctor || 0)}
              </Typography>
            </ItemRow>

            <ItemRow>
              <LabelWithIcon>
                <LocalHospitalOutlined color="primary" fontSize="small" />
                <div>
                  <Typography variant="body2" fontWeight={700}>
                    Hospital
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {surgerie.hospital?.company_name || 'Não informado'}
                  </Typography>
                </div>
              </LabelWithIcon>
              <Typography variant="caption" color="textSecondary">
                Valor
              </Typography>
              <Typography variant="body2" fontWeight={700} color="success.main">
                {moneyFormat(surgerie.value_hospital || 0)}
              </Typography>
            </ItemRow>

            <ItemRow>
              <LabelWithIcon>
                <EventSeatOutlined color="primary" fontSize="small" />
                <div>
                  <Typography variant="body2" fontWeight={700}>
                    Anestesista
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Valor sugerido pelo procedimento
                  </Typography>
                </div>
              </LabelWithIcon>
              <IconButton
                disabled={!checkPermission('surgeries.new.change-anesthetist-value')}
                onClick={() => setEditAnesthetistPrice(true)}
                size="small"
                color="primary"
              >
                <Edit fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight={700} color="success.main">
                {moneyFormat(surgerie.value_anesthetist || 0)}
              </Typography>
            </ItemRow>
          </SoftPanel>

          <TotalPanel>
            <TotalValue>
              <Typography variant="caption" color="textSecondary">
                Valor total
              </Typography>
              <Typography variant="h5" color={palette.primary} fontWeight={800}>
                {moneyFormat(surgerie.value_total)}
                <IconButton
                  onClick={() => setEditMargin(true)}
                  size="small"
                  color="primary"
                  aria-label="Editar valor total"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Typography>
            </TotalValue>

            <MiniRow>
              <Typography variant="body2" color="textSecondary">
                Custo do orçamento
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {moneyFormat(surgerie.value_budget)}
              </Typography>
            </MiniRow>

            <MiniRow>
              <Typography variant="body2" color="textSecondary">
                Margem de lucro
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {percentFormat(surgerie.profit_margin || 0)}
                <IconButton onClick={() => setEditMargin(true)} size="small" color="primary" aria-label="Editar margem">
                  <Edit fontSize="small" />
                </IconButton>
              </Typography>
            </MiniRow>

            <SoftPanel>
              <Typography variant="caption" color="textSecondary">
                Lucro
              </Typography>
              <Typography variant="body2" fontWeight={700} color="success.main">
                {moneyFormat(surgerie.value_markup || 0)}
              </Typography>
            </SoftPanel>
          </TotalPanel>
        </SummaryGrid>
      </CardBody>
    </GuidedCard>
  );
};

export default SurgerieSixthStep;
